// Phase 38 · 23b — the roster.
//
// A bar chart of five names answered "who resolved most" and nothing else. The
// row list answers the question the workload variant is actually asked: who is
// holding the queue right now, and who is holding nothing. Zero-load members
// stay on the list — an empty row is a staffing fact, not missing data, and
// dropping it is how a roster quietly overstates capacity.

import { duration } from '@shared/format/duration';
import { initials, identityColor, personName } from '@components/tickets/identity';
import { loadFinding } from '../../features/dashboard/widgetFindings';
import { WidgetShell, WidgetEmpty, Found } from './WidgetShell';

interface AgentData {
  name: string;
  ticketsResolved: number;
  openTickets?: number;
  avgResponseTime: number | null;
  satisfaction: number;
  /** Roster sub-line; absent when the roster query degraded. */
  role?: string;
}

interface AgentPerformanceWidgetProps {
  title?: string;
  data?: AgentData[];
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  className?: string;
  chartType?: 'bar' | 'horizontal-bar';
  /** Which metric to rank by: 'resolved' (default, leaderboard) or 'open' (current workload). */
  metric?: 'resolved' | 'open';
  /**
   * The dashboard's selected date range, used purely for the subtitle so the
   * caption matches the window the data was actually fetched for. The numbers
   * have always followed the picker (useAgentMetrics(dateRange) → BFF →
   * analytics-service); the caption used to be hardcoded to "this week", which
   * misreported every other range — including the year-to-date default.
   */
  dateRange?: { start: string; end: string };
}

/**
 * Parse a YYYY-MM-DD range bound into a LOCAL date.
 * `new Date('2026-01-01')` is parsed as UTC midnight, which renders as the
 * previous day for anyone west of UTC — so build the date from parts instead.
 */
function parseRangeDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** "30 Jun – 30 Jul 2026", or a single date when the range is one day. */
export function formatRangeLabel(range?: { start: string; end: string }): string | null {
  if (!range?.start || !range?.end) return null;
  const start = parseRangeDate(range.start);
  const end = parseRangeDate(range.end);
  if (!start || !end) return null;

  const format = (date: Date, withYear: boolean) =>
    date.toLocaleDateString(undefined, {
      day: 'numeric',
      month: 'short',
      ...(withYear ? { year: 'numeric' } : {}),
    });

  if (range.start === range.end) return format(end, true);
  const sameYear = start.getFullYear() === end.getFullYear();
  return `${format(start, !sameYear)} – ${format(end, true)}`;
}

/** Minutes → "2h 6m". Null stays an em-dash; 0 is not a real response time. */
function responseLabel(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined || minutes <= 0) return '—';
  return duration(minutes * 60);
}

export function AgentPerformanceWidget({
  data,
  loading = false,
  error,
  onRetry,
  className = '',
  title,
  metric = 'resolved',
  dateRange,
}: AgentPerformanceWidgetProps) {
  const isOpen = metric === 'open';
  const valueOf = (a: AgentData) => (isOpen ? a.openTickets ?? 0 : a.ticketsResolved || 0);

  // 'open' is a live snapshot of current workload, so it is genuinely not
  // date-scoped. 'resolved' follows the dashboard's date picker, so say which
  // window is on screen instead of claiming "this week".
  const rangeLabel = formatRangeLabel(dateRange);
  const subtitle = isOpen
    ? 'Sorted by open tickets held'
    : rangeLabel
      ? `Tickets resolved · ${rangeLabel}`
      : 'Tickets resolved';

  const rows = [...(data || [])].sort(
    (a, b) => valueOf(b) - valueOf(a) || a.name.localeCompare(b.name)
  );
  const max = rows.reduce((m, r) => Math.max(m, valueOf(r)), 0) || 1;
  const found = isOpen ? loadFinding(rows.map((r) => ({ name: r.name, open: valueOf(r) }))) : null;

  const finding = isOpen
    ? found
      ? (
          <>
            <Found>
              One agent holds {found.held} of {found.total}.
            </Found>{' '}
            {found.idle > 0
              ? `${found.idle} member${found.idle === 1 ? '' : 's'} holding nothing ${found.idle === 1 ? 'is' : 'are'} listed too — an empty row is a staffing fact.`
              : 'Only members holding tickets are listed.'}
          </>
        )
      : rows.length > 0
        ? (
            rows.some((r) => valueOf(r) === 0)
              ? <>Load is spread across the roster. Members holding nothing are listed too — an empty row is a staffing fact.</>
              : rows.length === 1
                ? <>One member holds all the open work. Only members holding tickets are listed.</>
                : <>Load is spread across the roster. Only members holding tickets are listed.</>
          )
        : null
    : null;

  return (
    <WidgetShell
      title={title || (isOpen ? 'Agent Workload' : 'Agent Performance')}
      subtitle={loading ? undefined : subtitle}
      loading={loading}
      error={error}
      onRetry={onRetry}
      className={className}
      finding={finding}
      bodyClassName="overflow-y-auto co-scrollbar-none"
      empty={
        rows.length === 0 ? (
          <WidgetEmpty
            title="No agent data"
            hint="Agents appear here once tickets are assigned to them."
          />
        ) : undefined
      }
    >
      {rows.map((agent, i) => {
        const value = valueOf(agent);
        return (
          <div
            key={`${agent.name}-${i}`}
            className={`flex items-center gap-3 px-[18px] py-[11px] ${
              i < rows.length - 1 ? 'border-b border-[var(--co-line-subtle)]' : ''
            }`}
          >
            <span
              className="flex h-[26px] w-[26px] flex-none items-center justify-center rounded-[7px] text-[10px] font-semibold text-[var(--co-ink-invert)]"
              style={{ background: identityColor(agent.name) }}
              aria-hidden
            >
              {initials(agent.name)}
            </span>

            <div className="min-w-0 flex-1">
              <div
                className={`truncate text-[13px] font-semibold ${
                  value === 0 ? 'text-[var(--co-ink-3)]' : 'text-[var(--co-ink)]'
                }`}
              >
                {personName(agent.name) || agent.name}
              </div>
              {agent.role && (
                <div className="co-mono mt-[3px] truncate text-[9.5px] uppercase tracking-[.08em] text-[var(--co-ink-3)]">
                  {agent.role}
                </div>
              )}
            </div>

            <div className="w-[124px] flex-none">
              <div className="flex items-center gap-2">
                <div className="h-[6px] flex-1 overflow-hidden rounded-[3px] bg-[var(--co-sunken)]">
                  <div
                    className="h-full rounded-[3px]"
                    style={{
                      width: `${Math.round((value / max) * 100)}%`,
                      background: found && agent.name === found.holder ? 'var(--co-burgundy)' : 'var(--co-ink)',
                    }}
                  />
                </div>
                <span
                  className={`co-mono w-5 flex-none text-right text-[12px] font-semibold ${
                    value === 0 ? 'text-[var(--co-ink-3)]' : 'text-[var(--co-ink)]'
                  }`}
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {value}
                </span>
              </div>
              {isOpen && (
                <div className="co-mono mt-[5px] text-[9px] uppercase tracking-[.08em] text-[var(--co-ink-3)]">
                  {agent.ticketsResolved || 0} resolved
                </div>
              )}
            </div>

            <div
              className="co-mono w-[52px] flex-none text-right text-[12px] text-[var(--co-ink-2)]"
              style={{ fontVariantNumeric: 'tabular-nums' }}
              title="Average first response"
            >
              {responseLabel(agent.avgResponseTime)}
            </div>
          </div>
        );
      })}
    </WidgetShell>
  );
}
